import { BackgroundTask, RevalueType } from '@/pages/tools/RevaluationTool/types';

/** Most-recent runs to request from the listing endpoint (poll + stream). */
export const TASK_POLL_LIMIT = 25;

export const REVALUE_TYPE_LABELS: Record<string, string> = {
  [RevalueType.VALUE_AND_METRICS]: 'Valuation',
  [RevalueType.BCVA]: 'BCVA',
};

export const revalueTypeLabel = (type?: string | null): string =>
  (type && REVALUE_TYPE_LABELS[type]) ?? type ?? '';

/** Marks a task produced by deliverable generation rather than revaluation. */
export const DELIVERABLE_TASK_KIND = 'DELIVERABLE';

export const isDeliverableTask = (task: BackgroundTask): boolean =>
  task.run_params?.task_kind === DELIVERABLE_TASK_KIND;

/**
 * Label for a task's type pill. The bell shows every producer that writes to the shared task table,
 * so this is the single place that decides how each one is named — the completion toasts use it too.
 */
export const taskKindLabel = (task: BackgroundTask): string => {
  if (isDeliverableTask(task)) {
    return task.run_params?.job_kind === 'MEMOS' ? 'Memos' : 'Deliverables';
  }
  return revalueTypeLabel(task.revalue_type);
};

/** Second line of a deliverable row: which entity, which date, how many reports. */
export const taskSubtitle = (task: BackgroundTask): string | null => {
  if (!isDeliverableTask(task)) return null;
  const { entity_name, run_date, report_types } = task.run_params ?? {};
  const parts: string[] = [];
  if (entity_name) parts.push(entity_name);
  if (run_date) parts.push(run_date);
  if (report_types?.length) {
    parts.push(`${report_types.length} report${report_types.length > 1 ? 's' : ''}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
};

/**
 * Reports that failed inside an otherwise successful run.
 *
 * These are deliberately not surfaced as `statistic.error`: the workbook is still produced from the
 * sheets that did build, and the user must keep the Download button for it.
 */
export const failedReportNames = (task: BackgroundTask): string[] =>
  (task.statistic?.build_report_results ?? []).flatMap((entry) =>
    Object.entries(entry)
      .filter(([, outcome]) => outcome !== 'success')
      .map(([name]) => name),
  );

/**
 * Whether a finished task actually left a file behind.
 *
 * A deliverable run advertises its result with an S3 pointer; a run that was cancelled, or whose
 * upload failed, has none. Revaluation publishes its log through a different mechanism that the task
 * listing does not expose, so it is assumed to have one — this must not change its behaviour.
 */
export const taskProducedResult = (task: BackgroundTask): boolean =>
  !isDeliverableTask(task) || Boolean(task.statistic?.s3_key);

/** Toast text for a task that just reached a terminal status. */
export const taskCompletionMessage = (task: BackgroundTask): string => {
  const label = taskKindLabel(task);
  if (isDeliverableTask(task)) {
    return task.statistic?.error
      ? `${label} failed: ${task.statistic.error}`
      : `${label} ready to download.`;
  }
  return task.statistic?.error
    ? `${label} revaluation failed: ${task.statistic.error}`
    : `${label} revaluation finished.`;
};

// Statuses the backend treats as terminal: the job is no longer in flight.
// `unknown` is the backend's "no longer tracked / failed" terminal state.
export const TERMINAL_TASK_STATUSES = ['finished', 'unknown'];

export const isTaskTerminal = (status?: string): boolean =>
  TERMINAL_TASK_STATUSES.includes((status ?? '').toLowerCase());

export const isTaskActive = (task: { status: string }): boolean =>
  !isTaskTerminal(task.status);
